import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate, useParams, useOutletContext } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { usePrintStore } from '@renderer/stores/printStore'
import {
  processAndUploadStrip,
  processAndUploadGIF,
  stateUploader
} from '@renderer/utils/pipeline/outputOrchestrator'
import { initiatePrint } from '@renderer/utils/pipeline/printService'
import type { ProcessingContext } from '@renderer/types/outputPipeline.types'
import { waitPrintResultMinDisplay } from '@renderer/utils/printResultMinDisplay'
import cogoToast from '@successtar/cogo-toast'
import PrintPreview, { PrintPreviewRef } from '@renderer/components/PrintPreview'
import StickerEditor from '@renderer/components/StickerEditor'
import { FrameOption, FrameConfig } from '@renderer/types/configState'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'
import { navigateAfterOutputsReady } from '@renderer/utils/navigateAfterOutputs'

interface UserContext {
  user?: {
    email: string
  }
}

interface Sticker {
  id: string
  src: string
  name: string
}

interface StickerPlacement {
  id: string
  stickerId: string
  x: number
  y: number
  size: number
  timestamp: number
}

interface StickerSize {
  id: string
  name: string
  size: number
  dotSize: number
}

const Select: React.FC = () => {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const context = useOutletContext<UserContext | null>()
  const {
    gallery,
    galleryKey,
    setSessionState,
    userPreferences,
    id
  } = useSessionStateStore()
  const { getFrame, frames } = useConfigStore()
  const { downloadLocal } = useSettingsStore()
  const { isPrintingEnabled, getPrinterForFrame } = usePrintStore()

  // States
  const [selectedStickerId, setSelectedStickerId] = useState<string>('1')
  const [selectedSizeId, setSelectedSizeId] = useState<string>('medium')
  const [stickerPlacements, setStickerPlacements] = useState<StickerPlacement[]>([])
  const [isProcessing, setIsProcessing] = useState(false)
  const [isGeneratingStrip, setIsGeneratingStrip] = useState(true)
  const [stripUrl, setStripUrl] = useState<string>('')
  const [undoStack, setUndoStack] = useState<StickerPlacement[][]>([])
  const [redoStack, setRedoStack] = useState<StickerPlacement[][]>([])

  const uploadStartedRef = useRef(false)

  const previewRef = useRef<PrintPreviewRef>(null)
  const isMountedRef = useRef(true)

  // Sticker sizes
  const stickerSizes: StickerSize[] = [
    { id: 'small', name: 'Small', size: 80, dotSize: 20 },
    { id: 'medium', name: 'Medium', size: 120, dotSize: 32 },
    { id: 'large', name: 'Large', size: 180, dotSize: 64 }
  ]

  // Generate stickers list (1.png to 41.png)
  const stickers: Sticker[] = Array.from({ length: 41 }, (_, i) => ({
    id: `${i + 1}`,
    src: `/assets/boothndaughter/stickers/${i + 1}.png`,
    name: `Sticker ${i + 1}`
  }))

  // Get frame configuration
  const getFrameConfig = (frame: FrameOption | null): FrameConfig => {
    if (!frame)
      return {
        id: '',
        baseImage: '',
        drawParams: {
          canvasWidth: 1200,
          canvasHeight: 1800,
          backgroundColor: '#FFFFFF',
          images: []
        }
      }
    return {
      id: frame._id,
      baseImage: frame.thumbnail,
      drawParams: frame.drawParams
    }
  }

  const selectedFrame = getFrame(userPreferences.frameId || frames[0]._id) || null
  const frameConfig = getFrameConfig(selectedFrame)

  // Get captured photos for preview
  const capturedPhotos = gallery.capturedItems
    .filter((item) => item.photoUrl)
    .map((item) => item.photoUrl)

  // Use the existing filtered strip from gallery
  useEffect(() => {
    // Initialize media uploads (GIF/MP4) on mount
    const initializeMedia = async (): Promise<void> => {
      // Prevent concurrent upload attempts
      if (uploadStartedRef.current) {
        return
      }
      uploadStartedRef.current = true

      if (userPreferences.useGIF) {
        (async (): Promise<void> => {
          try {
            const photoUrls = gallery.capturedItems
              .filter((item) => item.photoUrl)
              .map((item) => item.photoUrl!) as string[]
            const globalConfig = useConfigStore.getState().config
            const gifSpeed = globalConfig?.pages.capture?.settings?.gifSpeed || 1000
            const customGifFrame = globalConfig?.pages.capture?.settings?.customGifFrame
            const ctx: ProcessingContext = {
              context,
              projectId,
              sessionId: id || '',
              galleryId: galleryKey || gallery.id,
              downloadLocal,
              isOnline: true
            }
            const { gifLocalUrl, mp4LocalUrl } = await processAndUploadGIF(
              photoUrls,
              gifSpeed,
              ctx,
              customGifFrame
            )
            const newItems: Array<{
              id: string
              animationUrl: string
              animationType: 'gif' | 'mp4'
              gifUrl: string
              photoUrl: string
              timestamp: Date
            }> = []
            if (mp4LocalUrl) {
              newItems.push({
                id: `dreambooth-animation-${id}`,
                animationUrl: mp4LocalUrl,
                animationType: 'mp4',
                gifUrl: '',
                photoUrl: '',
                timestamp: new Date()
              })
            }
            if (gifLocalUrl) {
              newItems.push({
                id: `dreambooth-animation-gif-${id}`,
                animationUrl: gifLocalUrl,
                animationType: 'gif',
                gifUrl: gifLocalUrl,
                photoUrl: '',
                timestamp: new Date()
              })
            }
            if (newItems.length > 0) {
              setSessionState({
                gallery: {
                  capturedItems: [...gallery.capturedItems, ...newItems]
                }
              })
            }
          } catch (error) {
            console.error('[Select] Failed to initialize GIF/MP4 media:', error)
          }
        })()
      }
    }

    initializeMedia()

    if (gallery.stripUrl) {
      setStripUrl(gallery.stripUrl)
      setIsGeneratingStrip(false)
    } else {
      // Fallback: generate strip if none exists
      const generateStrip = async (): Promise<void> => {
        setIsGeneratingStrip(true)
        try {
          // Wait a bit for PrintPreview to be ready
          await new Promise((resolve): void => {
            setTimeout(resolve, 1000)
          })

          // Generate the strip
          const stripBlob = await previewRef.current?.exportImage()
          if (stripBlob) {
            const stripUrl = URL.createObjectURL(stripBlob)
            setStripUrl(stripUrl)
          } else {
            throw new Error('Failed to generate strip')
          }
        } catch (error) {
          console.error('Error generating strip:', error)
          cogoToast.error('Failed to generate photo strip')
        } finally {
          setIsGeneratingStrip(false)
        }
      }

      generateStrip()
    }
  }, [gallery.stripUrl])

  useEffect(() => {
    isMountedRef.current = true
    return (): void => {
      isMountedRef.current = false
    }
  }, [])

  // Handle sticker placement with useCallback to prevent unnecessary re-renders
  const handleStickerPlacement = useCallback(
    (x: number, y: number): void => {
      const selectedSize = stickerSizes.find((size) => size.id === selectedSizeId)
      if (!selectedSize) return

      const newPlacement: StickerPlacement = {
        id: `sticker-${Date.now()}-${Math.random()}`,
        stickerId: selectedStickerId,
        x,
        y,
        size: selectedSize.size,
        timestamp: Date.now()
      }

      // Add to undo stack
      setUndoStack((prev) => [...prev, [...stickerPlacements]])
      setRedoStack([]) // Clear redo stack when new action is performed

      setStickerPlacements((prev) => [...prev, newPlacement])
    },
    [selectedSizeId, selectedStickerId, stickerPlacements]
  )

  // Undo functionality
  const handleUndo = (): void => {
    if (undoStack.length === 0) return

    const previousState = undoStack[undoStack.length - 1]
    const newUndoStack = undoStack.slice(0, -1)

    setRedoStack((prev) => [...prev, stickerPlacements])
    setUndoStack(newUndoStack)
    setStickerPlacements(previousState)
  }

  // Redo functionality
  const handleRedo = (): void => {
    if (redoStack.length === 0) return

    const nextState = redoStack[redoStack.length - 1]
    const newRedoStack = redoStack.slice(0, -1)

    setUndoStack((prev) => [...prev, stickerPlacements])
    setRedoStack(newRedoStack)
    setStickerPlacements(nextState)
  }

  // Handle continue to result
  const handleContinue = async (): Promise<void> => {
    setIsProcessing(true)
    const processingStartedAt = Date.now()
    let didAttemptPrint = false

    try {
      // Create a canvas to combine strip with stickers
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      if (!ctx) throw new Error('Could not get canvas context')

      // Load the strip image
      const stripImg = new Image()
      stripImg.crossOrigin = 'anonymous'

      await new Promise<void>((resolve, reject): void => {
        stripImg.onload = (): void => resolve()
        stripImg.onerror = (): void => reject(new Error('Failed to load strip image'))
        stripImg.src = stripUrl
      })

      // Set canvas size to match strip dimensions
      canvas.width = stripImg.naturalWidth
      canvas.height = stripImg.naturalHeight

      // Draw the strip
      ctx.drawImage(stripImg, 0, 0)

      // Draw stickers
      for (const placement of stickerPlacements) {
        try {
          const stickerImg = new Image()
          stickerImg.crossOrigin = 'anonymous'

          await new Promise<void>((resolve, reject): void => {
            stickerImg.onload = (): void => resolve()
            stickerImg.onerror = (): void =>
              reject(new Error(`Failed to load sticker ${placement.stickerId}`))
            stickerImg.src = `/assets/boothndaughter/stickers/${placement.stickerId}.png`
          })

          // Calculate sticker position (center the sticker at the placement point)
          const stickerX = placement.x - placement.size / 2
          const stickerY = placement.y - placement.size / 2

          ctx.drawImage(stickerImg, stickerX, stickerY, placement.size, placement.size)
        } catch (error) {
          console.error('Failed to render sticker:', error)
        }
      }

      // Convert to blob
      const finalBlob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (blob) resolve(blob)
            else reject(new Error('Failed to create final image blob'))
          },
          'image/jpeg',
          0.95
        )
      })

      const stripCtx: ProcessingContext = {
        context,
        projectId,
        sessionId: id || '',
        galleryId: galleryKey || gallery.id,
        downloadLocal,
        isOnline: true
      }
      const stripResult = await processAndUploadStrip(finalBlob, stripCtx)
      const localUrl = stripResult?.localUrl ?? URL.createObjectURL(finalBlob)
      setSessionState({
        gallery: {
          stripUrl: localUrl
        }
      })

      if (isPrintingEnabled && userPreferences.usePrint && userPreferences.copies > 0) {
        didAttemptPrint = true
        const { printerName, settings } = getPrinterForFrame(userPreferences.frameId || '')
        try {
          await initiatePrint(
            finalBlob,
            printerName,
            userPreferences.copies,
            settings,
            true
          )
          cogoToast.success('Printing Initialized')
        } catch (err) {
          cogoToast.error(`Failed to print: ${err instanceof Error ? err.message : 'Unknown error'}`)
        }
      }

      const minDisplaySec = useSettingsStore.getState().printResultMinDisplaySeconds
      if (didAttemptPrint && minDisplaySec > 0) {
        await waitPrintResultMinDisplay(processingStartedAt, minDisplaySec, {
          isStillMounted: () => isMountedRef.current
        })
      }

      stateUploader.uploadSessionState()
      navigateAfterOutputsReady(navigate, projectId!, useConfigStore.getState().config)
    } catch (error) {
      console.error('Error in handleContinue:', error)
      cogoToast.error('Failed to process strip with stickers')
      stateUploader.uploadSessionState()
      navigateAfterOutputsReady(navigate, projectId!, useConfigStore.getState().config)
    } finally {
      setIsProcessing(false)
    }
  }

  // Call all hooks at the top level
  const cachedEditBg = useCachedBackgroundImage('/assets/boothndaughter/edit.webp')

  return (
    <div
      className="w-full h-full relative overflow-hidden"
      style={{
        backgroundImage: `url(${cachedEditBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Done Button - Top Right */}
      <button
        onClick={handleContinue}
        disabled={isProcessing || isGeneratingStrip}
        className="absolute top-8 right-8 z-40 transition-transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          width: '179px',
          height: '107px'
        }}
      >
        <img
          src="/assets/boothndaughter/done.webp"
          alt="Done"
          className="w-full h-full object-contain"
          onError={(e) => {
            console.error('Failed to load done button image')
            e.currentTarget.style.display = 'none'
          }}
        />
      </button>

      {/* Processing Overlay */}
      {isProcessing && (
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white/90 px-8 py-6 rounded-2xl shadow-2xl">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Processing...</h2>
              <p className="text-gray-600">Please wait while we process your strip</p>
            </div>
          </div>
        </div>
      )}

      {/* Generating Strip Overlay */}
      {isGeneratingStrip && (
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white/90 px-8 py-6 rounded-2xl shadow-2xl">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Generating Strip...</h2>
              <p className="text-gray-600">Please wait while we prepare your photo strip</p>
            </div>
          </div>
        </div>
      )}

      {/* Left Pane - StickerEditor */}
      <div className="absolute left-8 top-8 bottom-8 w-[640px]">
        {stripUrl ? (
          <StickerEditor
            stripUrl={stripUrl}
            stickerPlacements={stickerPlacements}
            onStickerPlacement={handleStickerPlacement}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-white text-xl">Loading photo strip...</div>
          </div>
        )}
      </div>

      {/* Center Pane - Controls */}
      <div className="absolute top-[250px] left-[750px] bottom-8 w-42 flex flex-col">
        {/* Undo/Redo Buttons */}
        <div className="flex items-center justify-center p-4 mb-16 gap-4">
          <button
            title="Undo"
            onClick={handleUndo}
            disabled={undoStack.length === 0}
            className="transition-transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              width: '60px',
              height: '60px'
            }}
          >
            <img
              src="/assets/boothndaughter/undo.webp"
              alt="Undo"
              className="w-full h-full object-contain"
              onError={(e) => {
                console.error('Failed to load undo button image')
                e.currentTarget.style.display = 'none'
              }}
            />
          </button>
          <button
            title="Redo"
            onClick={handleRedo}
            disabled={redoStack.length === 0}
            className="transition-transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              width: '60px',
              height: '60px'
            }}
          >
            <img
              src="/assets/boothndaughter/redo.webp"
              alt="Redo"
              className="w-full h-full object-contain"
              onError={(e) => {
                console.error('Failed to load redo button image')
                e.currentTarget.style.display = 'none'
              }}
            />
          </button>
        </div>

        {/* Sticker Size Options */}
        <div className="mb-8">
          <div className="flex flex-col gap-3">
            {stickerSizes.map((size) => (
              <button
                key={size.id}
                onClick={() => setSelectedSizeId(size.id)}
                className={`flex flex-col gap-2 items-center justify-center p-3 rounded-lg transition-all ${
                  selectedSizeId === size.id
                    ? 'bg-white/50 border-2 border-blue-400'
                    : 'bg-white/20 hover:bg-white/30'
                }`}
              >
                <div
                  className="rounded-full bg-blue-500"
                  style={{
                    width: `${size.dotSize}px`,
                    height: `${size.dotSize}px`
                  }}
                />
                <span className="text-grey font-medium">{size.name}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Right Pane - Stickers */}
      <div className="absolute right-36 top-[340px] bottom-8 w-[40%]">
        <div className="grid grid-cols-8 gap-3 overflow-y-auto max-h-[600px]">
          {stickers.map((sticker) => (
            <button
              key={sticker.id}
              onClick={() => setSelectedStickerId(sticker.id)}
              className={`p-3 rounded-lg transition-all ${
                selectedStickerId === sticker.id
                  ? 'bg-blue-600/80 border-2 border-blue-400'
                  : 'bg-white/20 hover:bg-white/30'
              }`}
            >
              <img
                src={sticker.src}
                alt={sticker.name}
                className="w-full h-auto"
                onError={(e) => {
                  console.error(`Failed to load sticker: ${sticker.src}`)
                  e.currentTarget.style.display = 'none'
                }}
              />
            </button>
          ))}
        </div>
      </div>

      {/* Hidden PrintPreview for generating strip */}
      <div className="hidden">
        <PrintPreview
          ref={previewRef}
          frameConfig={frameConfig}
          selectedPhotos={capturedPhotos}
          isInteractive={false}
          sessionId={galleryKey || gallery.id}
        />
      </div>
    </div>
  )
}

export default Select
